import _regeneratorRuntime from '@babel/runtime/regenerator';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import { create } from 'apisauce';
import { string, object, array } from 'yup';

var baseStaticConfig = {
  defaultHeaders: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-bpi-service-context': 'USER',
    // todo confirm this
    'X-TS-AJAX-Request': true
  }
};

/** @param {number} value */
var fix = function fix(value) {
  return ("0" + value).slice(-2);
};
/**
 * @param {Date} date
 */


var formatDate = function formatDate(date) {
  return date.getFullYear() + "-" + fix(date.getMonth() + 1) + "-" + fix(date.getDate()) + "T" + fix(date.getHours()) + ":" + fix(date.getMinutes()) + ":" + fix(date.getSeconds());
};
/**
 * @param {import('types').BankId | null} bankId
 * @param {string} [appId]
 */


var getAppTitle = function getAppTitle(bankId, appId) {
  switch (appId) {
    case 'CustomerNumberAssist':
      return appId;

    default:
      if (process.env.NODE_ENV !== 'production') {
        if (!Boolean(bankId)) {
          throw new Error("bankId is not valid. Value: " + JSON.stringify(bankId));
        }
      }

      return bankId + " Web";
  }
};
/**
 * @param {import('types').BankId | null} bankId
 * @param {string | null} [userTrackingId]
 * @param {string} [appId]
 */


var setClientContext = function setClientContext(bankId, userTrackingId, appId) {
  return JSON.stringify({
    client: {
      user_tracking_id: userTrackingId || '',
      app_title: getAppTitle(bankId, appId),
      app_package_name: 'WEB'
    },
    env: {
      platform: 'WEB',
      // @ts-ignore
      platform_version: navigator.appVersion || navigator.vendor || window.opera,
      // @ts-ignore
      make: navigator.platform || window.opera,
      locale: navigator.language
    }
  });
};
/**
 * @param {string} clientContext
 * @param {import('types').BankId} [bankId]
 * @param {string} [customerNumber]
 * @param {number} [apiTrustLevel]
 * @param {string} [apiTrustLevelCreatedDate]
 */

var setStaffHeaders = function setStaffHeaders(clientContext, bankId, customerNumber, apiTrustLevel, apiTrustLevelCreatedDate) {
  return {
    'x-bpi-client-context': clientContext,
    'x-bpi-customer-id': customerNumber,
    'x-bpi-trust-level': apiTrustLevel,
    'x-bpi-trust-created-dt': apiTrustLevelCreatedDate || formatDate(new Date()),
    'x-bpi-customer-bank-id': bankId
  };
};
/**
 * @param {string} clientContext
 */

var setUserHeaders = function setUserHeaders(clientContext) {
  return {
    'x-bpi-client-context': clientContext
  };
};
/**
 * Add appversion props in client context object of headers
 * @param {import('apisauce').HEADERS} headers
 */

var setAppVersionHeaders = function setAppVersionHeaders(headers) {
  var newHeaders = _objectSpread({}, headers);

  var clientContext = JSON.parse(newHeaders['x-bpi-client-context']);
  clientContext.client = _objectSpread(_objectSpread({}, clientContext.client), {}, {
    app_version_code: navigator.appVersion,
    app_version_name: navigator.appName
  });
  newHeaders['x-bpi-client-context'] = JSON.stringify(clientContext);
  return newHeaders;
};

/**
 * Init our main API connector. These are transient and will be instantiated as required.
 *
 * The microservices require standard header format so we take care of that for each one created. There are 2 types of header
 * format staff and user.
 *
 * @param {string} baseUrl Base URL for the API.
 * @param {import('types').BankId} bankId - a string of 'CB' || 'YB' this is added as a global prop of apisauce and referenced in each endpoint url
 * @param {string | null} [userTrackingId]
 * @returns {import('apisauce').ApisauceInstance}
 */

var initApiConnector = function initApiConnector(baseUrl, bankId, userTrackingId) {
  var clientContext = setClientContext(bankId, userTrackingId);
  var dynamicHeaders = setUserHeaders(clientContext); // todo check these headers are correct

  return create({
    baseURL: baseUrl,
    headers: _objectSpread(_objectSpread({}, baseStaticConfig.defaultHeaders), dynamicHeaders)
  });
};
/**
 * Init our non branded API connector. These are transient and will be instantiated as required.
 *
 * The microservices require standard header format so we take care of that for each one created. This is brand agnostic
 *
 * @param {string} baseUrl Base URL for the API.
 * @param {string} [appId]
 * @returns {import('apisauce').ApisauceInstance}
 */

var initNoBrandApiConnector = function initNoBrandApiConnector(baseUrl, appId) {
  var clientContext = setClientContext(null, null, appId);
  var dynamicHeaders = setUserHeaders(clientContext); // todo check these headers are correct

  return create({
    baseURL: baseUrl,
    headers: _objectSpread(_objectSpread({}, baseStaticConfig.defaultHeaders), dynamicHeaders)
  });
};
/**
 * Init our staff only API connector. These are transient and will be instantiated as required.
 *
 * The microservices require standard header format so we take care of that for each one created.
 *
 * @param {string} baseUrl - a string for Base Url
 * @param {import('types').BankId} bankId - a string of 'CB' || 'YB' this is added as a global prop of apisauce and referenced in each endpoint url
 * @param {string} customerNumber - a string for customer number
 * @param {number} apiTrustLevel - a number for api trust level
 * @param {string | null} [userTrackingId] - a string for user tracking
 * @returns {import('apisauce').ApisauceInstance}
 */

var initApiConnectorStaffOnly = function initApiConnectorStaffOnly(baseUrl, bankId, customerNumber, apiTrustLevel, userTrackingId) {
  if (userTrackingId === void 0) {
    userTrackingId = null;
  }

  var clientContext = setClientContext(bankId, userTrackingId);
  var dynamicHeaders = setStaffHeaders(clientContext, bankId, customerNumber, apiTrustLevel);
  return create({
    baseURL: baseUrl,
    headers: _objectSpread(_objectSpread({}, baseStaticConfig.defaultHeaders), dynamicHeaders)
  });
};
/**
 * DEBT this is created this way to allow easy searching on methods once deprecated.
 * Avoid the temptation to consolidate this with the main init func - CM
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string | null} [userTrackingId]
 */

var initLegacyApiConnector = function initLegacyApiConnector(baseUrl, bankId, userTrackingId) {
  if (userTrackingId === void 0) {
    userTrackingId = null;
  }

  var api = initApiConnector(baseUrl, bankId, userTrackingId);
  api.setBaseURL(baseUrl);
  return api;
};

var log = {
  /** @param {any} error */
  // eslint-disable-next-line no-console,
  logError: function logError(error) {
    return console.error(error);
  },

  /** @param {string} customMessage */
  // eslint-disable-next-line no-console,
  logWarn: function logWarn(customMessage) {
    return console.warn(customMessage);
  }
};

var API_REQUEST_ERROR = 'Something went wrong while calling endpoint, Please check error logs';

/**
 * @param {import('yup').Schema<T>} schema
 * @param {T} model
 * @returns {string | import('yup').ValidationError}
 * @template T
 */
function validate(schema, model) {
  try {
    schema.validateSync(model, {
      abortEarly: true
    });
    return '';
  } catch (
  /** @type {import('yup').ValidationError} */
  error) {
    return error;
  }
}

// from swagger docs
var guidRegex = /^([a-fA-F0-9]{8}(-[a-fA-F0-9]{4}){3}-[a-fA-F0-9]{12})$/;
var httpsRegex = /^https:(\/){2}/;
var decisionReferenceRegex = /^([a-fA-F0-9-]{1,15})$/;

/** @type {import('yup').TestOptions} */

var isGuid = {
  // eslint-disable-next-line no-template-curly-in-string
  message: '${path} must be a valid GUID',
  test: function test(value) {
    return value ? guidRegex.test(value) : false;
  }
};
/** @type {import('yup').TestOptions} */

var isHttps = {
  // eslint-disable-next-line no-template-curly-in-string
  message: '${path} must be a valid uri with a scheme matching the https pattern',
  test: function test(value) {
    return value ? httpsRegex.test(value) : false;
  }
};
/** @type {import('yup').TestOptions} */

var isDecisionReference = {
  // eslint-disable-next-line no-template-curly-in-string
  message: '${path} must have a valid Decision Reference ',
  test: function test(value) {
    return value ? decisionReferenceRegex.test(value) : false;
  }
};

var getUuidSchema = function getUuidSchema() {
  return string().required().test(isGuid);
};
/**
 *
 * @param {import('types').BankId} bankId
 */


var validateBankId = function validateBankId(bankId) {
  var bankIdSchema = string().required().oneOf(['CB', 'YB']).label('Bank Id');
  return validate(bankIdSchema, bankId);
};
/** @param {string} type */

var validateAccountsScopeId = function validateAccountsScopeId(type) {
  var typeSchema = string().required().oneOf(['User', 'Customer']).label("Url parameter 'type'");
  return validate(typeSchema, type);
};
var isLocalhost = /^http(s)?:\/\/localhost:\d+/;
/** @param {string} apiUrl */

var validateApiUrl = function validateApiUrl(apiUrl) {
  if (isLocalhost.test(apiUrl)) {
    return '';
  }

  var urlSchema = string().required().url().test(isHttps);
  var apiUrlSchema = urlSchema.label('Url');
  return validate(apiUrlSchema, apiUrl);
};
/** @param {string} uuid */

var validateAccessToken = function validateAccessToken(uuid) {
  var accessTokenSchema = getUuidSchema().label('Access token');
  return validate(accessTokenSchema, uuid);
};
/** @param {string} uuid */

var validateUserTrackingId = function validateUserTrackingId(uuid) {
  var trackingIdSchema = getUuidSchema().label('User tracking id');
  return validate(trackingIdSchema, uuid);
};
/** @param {string} type */

var validateBenType = function validateBenType(type) {
  var benTypeSchema = string().required().oneOf(['ALL', 'UK', 'IFT']).label('Beneficiary type');
  return validate(benTypeSchema, type);
};
/** @param {string} id */

var validateDecisionReference = function validateDecisionReference(id) {
  var decisionReferenceSchema = string().required().test(isDecisionReference).label('Decision reference');
  return validate(decisionReferenceSchema, id);
};

/**
 * @param {string | import('yup').ValidationError} error
 * @returns {error is import('yup').ValidationError}
 */

function isError(error) {
  return error !== '';
}
/**
 * @param {Array<string | import('yup').ValidationError>} errors
 */


function filterErrors(errors) {
  return errors.filter(isError);
}
/**
 * @param {Array<string | import('yup').ValidationError>} errors
 */


function handleErrorLogging(errors) {
  console.group('API Connector: Validation Error');
  filterErrors(errors).forEach(function (error) {
    console.group(error.name + ": " + error.message);
    console.log("value: " + JSON.stringify(error.value));
    console.groupCollapsed('Stack');
    console.error(error);
    console.groupEnd();
    console.groupEnd();
  });
  console.groupEnd();
}
/**
 * @param {Array<string | import('yup').ValidationError>} errors
 */


function getErrorResponse(errors) {
  var filtered = filterErrors(errors);
  return filtered.length > 0 ? {
    error: filtered.join(',\n')
  } : {
    error: false
  };
}
/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param  {...string | import('yup').ValidationError} otherErrors
 */


var validateCommonMethodParams = function validateCommonMethodParams(baseUrl, bankId, token) {
  for (var _len = arguments.length, otherErrors = new Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
    otherErrors[_key - 3] = arguments[_key];
  }

  var errors = [validateApiUrl(baseUrl), validateBankId(bankId), validateAccessToken(token)].concat(otherErrors);
  handleErrorLogging(errors);
  return getErrorResponse(errors);
};
/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param  {...string | import('yup').ValidationError} otherErrors
 */

var validateParamsExceptToken = function validateParamsExceptToken(baseUrl, bankId) {
  for (var _len2 = arguments.length, otherErrors = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
    otherErrors[_key2 - 2] = arguments[_key2];
  }

  var errors = [validateApiUrl(baseUrl), validateBankId(bankId)].concat(otherErrors);
  handleErrorLogging(errors);
  return getErrorResponse(errors);
};
/**
 * @param {string} baseUrl
 * @param  {...string} otherErrors
 */

var validateNoBrandCommonMethodParams = function validateNoBrandCommonMethodParams(baseUrl) {
  for (var _len3 = arguments.length, otherErrors = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
    otherErrors[_key3 - 1] = arguments[_key3];
  }

  var errors = [validateApiUrl(baseUrl)].concat(otherErrors);
  handleErrorLogging(errors);
  return getErrorResponse(errors);
};

/**
 * @param {string} endpoint
 * @param {import("types").BankId} bankId
 * @param {string} scope
 * @param {any} type
 * @param {string} accountId
 */
var endpointParser = function endpointParser(endpoint, bankId, scope, type, accountId) {
  if (accountId === void 0) {
    accountId = '';
  }

  return endpoint.replace(':bankId', bankId).replace(':scope', scope).replace(':type', type || false).replace(':accountId', accountId);
};

var getAccountsListPath="/banks/:bankId/accounts/:scopeId?type=:type";var getAccountDetailsPath="/banks/:bankId/accounts/:scope/:accountId";

/**
 * @param {import('apisauce').ApiErrorResponse<T>} response
 * @template T
 */

function parseError(response) {
  return {
    isError: true,
    error: response.problem || API_REQUEST_ERROR,
    status: response.status,
    response: response
  };
}

/**
 * @param {import("apisauce").ApiResponse<T>} response
 * @template T
 */

function parseResponse(response) {
  if (response.ok) {
    return response.data;
  }

  return parseError(response);
}

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} type must be User || Customer
 */

var getAccountList = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, type, token) {
    var validations, apic, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateAccountsScopeId(type));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            apic.setHeaders(_objectSpread(_objectSpread({}, apic.headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = endpointParser(getAccountsListPath, bankId, 'default', type);
            _context.next = 10;
            return apic.get(path);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));

  return function getAccountList(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

var X_0_8_0 = '0.8.0';
var X_1_0_0 = '1.0.0';
var X_1_2_0 = '1.2.0';
var X_1_1_0 = '1.1.0';

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} accountId
 */

var getAccountDetails = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, accountId, userTrackingId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_0_8_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser(getAccountDetailsPath, bankId, 'default', false, accountId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getAccountDetails(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getAccountList: getAccountList,
  getAccountDetails: getAccountDetails
});

var addressSearch="/ref/address/search/:postCode";var addressSearchById="/ref/address/:addressId";

/**
 * @typedef {Object} AddressSearchResult
 * @property {string} id
 * @property {string} potential_address
 */

/**
 * @param {{ short_addresses: Array<AddressSearchResult> } | { id: string; address_line1: string;  }} data
 * @returns {{ short_addresses: Array<AddressSearchResult> }}
 */

function mapData(data) {
  if ('short_addresses' in data) {
    return data;
  }

  return {
    short_addresses: [{
      id: data.id,
      potential_address: data.address_line1
    }]
  };
}
/**
 * @param {string} baseUrl
 * @param {string} postCode
 */


var getAddressDetails = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, postCode) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            // @ts-ignore
            apic = initApiConnector(baseUrl, undefined);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0
            }));
            path = addressSearch.replace(':postCode', postCode.replace(/\s/g, ''));
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;

            if (!resp.ok) {
              _context.next = 14;
              break;
            }

            return _context.abrupt("return", mapData(resp.data));

          case 14:
            return _context.abrupt("return", parseError(resp));

          case 17:
            _context.prev = 17;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 21:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 17]]);
  }));

  return function getAddressDetails(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} baseUrl
 * @param {string} addressId
 */

var getAddressDetailsById = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, addressId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            // @ts-ignore
            apic = initApiConnector(baseUrl, undefined);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0
            }));
            path = addressSearchById.replace(':addressId', addressId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getAddressDetailsById(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getAddressDetails: getAddressDetails,
  getAddressDetailsById: getAddressDetailsById
});

/**
 * @param {string} endpoint
 * @param {import('types').BankId} bankId
 * @param {string} scope
 * @param {string} benType
 * @param {string} search
 * @param {string} page
 * @param {string} size
 */
var endpointParser$1 = function endpointParser(endpoint, bankId, scope, benType, search, page, size) {
  return endpoint.replace(':bankId', bankId).replace(':scope', scope).replace(':benType', benType).replace(':search', search).replace(':page', page).replace(':size', size);
};

var getAllBeneficiariesPath="/banks/:bankId/accounts/:scopeId/beneficiaries?benType=:benType&search=:search&page=:page&size=:size";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} benType must be ALL/IFT/UK
 * @param {string} search
 * @param {string} page
 * @param {string} size
 */

var getAllBeneficiaries = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, benType, search, page, size) {
    var validations, apic, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (search === void 0) {
              search = '';
            }

            if (page === void 0) {
              page = '';
            }

            if (size === void 0) {
              size = '';
            }

            _context.prev = 3;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 8;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateBenType(benType));

            if (!validations.error) {
              _context.next = 8;
              break;
            }

            return _context.abrupt("return", validations);

          case 8:
            apic = initApiConnector(baseUrl, bankId);
            apic.setHeaders(_objectSpread(_objectSpread({}, apic.headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = endpointParser$1(getAllBeneficiariesPath, bankId, 'default', benType, search, page, size);
            _context.next = 13;
            return apic.get(path);

          case 13:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 17:
            _context.prev = 17;
            _context.t0 = _context["catch"](3);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 21:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[3, 17]]);
  }));

  return function getAllBeneficiaries(_x, _x2, _x3, _x4, _x5, _x6, _x7) {
    return _ref.apply(this, arguments);
  };
}();

var index$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getAllBeneficiaries: getAllBeneficiaries
});

var createDIPCasePath="/banks/:bankId/cases/dip";var updateDIPCasePath="/banks/:bankId/cases/dip/:caseId";var saveDIPCasePath="/banks/:bankId/cases/dip/:caseId/savecase";var retrieveDIPCasePath="/banks/:bankId/cases/dip/:uuid/retrievecase";var createCSAPCasePath="/banks/:bankId/cases/csap/savings/existing";var updateCSAPCasePath="/banks/:bankId/cases/csap/savings/existing/:caseId/tasks/capture/actions/update";var submitCSAPCasePath="/banks/:bankId/cases/csap/savings/existing/:caseId/tasks/capture/actions/submit";var getNextTaskPath="/banks/:bankId/cases/csap/existing/:caseId/tasks/next";var getOffersPath="/banks/:bankId/cases/csap/existing/:caseId/tasks/offers";var attachCaseEvidencePath="/banks/:bankId/cases/csap/existing/:caseId/evidence";var csapOfferDecisionPath="/banks/:bankId/cases/csap/existing/:caseId/tasks/offers/actions/decision";var getTaskCompletedPath="/banks/:bankId/cases/csap/existing/:caseId/tasks/completed";var cisaBalanceTransferPath="/banks/:bankId/cases/cisa/transfer";var attachCashISAEvidencePath="/banks/:bankId/cases/cisa/transfer/:caseId/evidence";

/**
 * @param {string} endpoint
 * @param {import("types").BankId} bankId
 * @param {string} [caseId]
 */
var endpointParser$2 = function endpointParser(endpoint, bankId, caseId) {
  var parsed = endpoint.replace(':bankId', bankId);

  if (caseId) {
    parsed = parsed.replace(':caseId', caseId);
  }

  return parsed;
};
/**
 * @param {string} endpoint
 * @param {import("types").BankId} bankId
 * @param {string} uuid
 */

var endpointParserRetrive = function endpointParserRetrive(endpoint, bankId, uuid) {
  return endpoint.replace(':bankId', bankId).replace(':uuid', uuid);
};

/**
 * This will use to create DIP case id
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userTrackingId
 * @param {object} body
 */

var createDIPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_2_0
            }));
            path = endpointParser$2(createDIPCasePath, bankId);
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function createDIPCase(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to update applicant details with DIP case id and return task id, eligible amount,eligible product
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var updateDIPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_2_0
            }));
            path = endpointParser$2(updateDIPCasePath, bankId, caseId);
            _context.next = 11;
            return apic.put(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function updateDIPCase(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to update applicant details with DIP case id and return task id, eligible amount,eligible product
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var saveDIPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_2_0
            }));
            path = endpointParser$2(saveDIPCasePath, bankId, caseId);
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function saveDIPCase(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to retrieve DIP case id
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userTrackingId
 * @param {string} uuid
 */

var retrieveDIPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, uuid) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_2_0
            }));
            path = endpointParserRetrive(retrieveDIPCasePath, bankId, uuid);
            _context.next = 11;
            return apic.get(path, uuid);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function retrieveDIPCase(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to create CSAP case id
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {object} body
 */

var createCSAPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser$2(createCSAPCasePath, bankId);
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function createCSAPCase(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to update applicant details for a CSAP case
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var updateCSAPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser$2(updateCSAPCasePath, bankId, caseId);
            _context.next = 11;
            return apic.put(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function updateCSAPCase(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to create CSAP case id
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var submitCSAPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser$2(submitCSAPCasePath, bankId, caseId);
            _context.next = 11;
            return apic.put(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function submitCSAPCase(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will be used to Get Next Task associated with CSAP case
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 */

var getNextTask = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser$2(getNextTaskPath, bankId, caseId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getNextTask(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will be used to Get Offers associated with CSAP case
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 */

var getOffers = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser$2(getOffersPath, bankId, caseId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getOffers(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will be used to attach evidence to the submitted CSAP case
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var attachCaseEvidence = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser$2(attachCaseEvidencePath, bankId, caseId);
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function attachCaseEvidence(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This service is used to capture customers decision on the presented offers
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var csapOfferDecision = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser$2(csapOfferDecisionPath, bankId, caseId);
            _context.next = 11;
            return apic.put(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function csapOfferDecision(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will be used to Get Task - Completed associated with CSAP case
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 */

var getTaskCompleted = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser$2(getTaskCompletedPath, bankId, caseId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getTaskCompleted(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * A balance transfer service used by cash-transfer-in webapp
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {object} body
 */

var cisaBalanceTransfer = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_1_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser$2(cisaBalanceTransferPath, bankId);
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function cisaBalanceTransfer(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will be used to attach evidence to the submitted cash ISA transfer in request.
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var attachCashISATransferEvidence = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_1_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParser$2(attachCashISAEvidencePath, bankId, caseId);
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function attachCashISATransferEvidence(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createDIPCase: createDIPCase,
  updateDIPCase: updateDIPCase,
  saveDIPCase: saveDIPCase,
  retrieveDIPCase: retrieveDIPCase,
  createCSAPCase: createCSAPCase,
  updateCSAPCase: updateCSAPCase,
  submitCSAPCase: submitCSAPCase,
  getNextTask: getNextTask,
  getOffers: getOffers,
  attachCaseEvidence: attachCaseEvidence,
  csapOfferDecision: csapOfferDecision,
  getTaskCompleted: getTaskCompleted,
  cisaBalanceTransfer: cisaBalanceTransfer,
  attachCashISATransferEvidence: attachCashISATransferEvidence
});

var getCustomerByIdPath="/banks/:bankId/customers/:customerId";var getCustomerListPath="/banks/:bankId/customers";var updatePersonalCustomerPath="/banks/:bankId/customers/personal/:customerId";var updatePersonalCustomerPersonalDetailsPath="/banks/:bankId/customers/personal/:customerId/personaldetails?duplicate_check=default";var createPersonalCustomerPath="/banks/:bankId/customers/personal?duplicate_check=default";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 */
// todo why is this passing an object?

var getCustomerList = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initLegacyApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token,
              'x-bpi-version': X_1_0_0
            }));
            path = getCustomerListPath.replace(':bankId', bankId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getCustomerList(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} endpoint
 * @param {import("types").BankId} bankId
 * @param {string} customerId
 */
var endpointParser$3 = function endpointParser(endpoint, bankId, customerId) {
  return endpoint.replace(':bankId', bankId).replace(':customerId', customerId);
};

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} customerId
 */

var getCustomerById = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, customerId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initLegacyApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token,
              'x-bpi-version': X_1_0_0
            }));
            path = endpointParser$3(getCustomerByIdPath, bankId, customerId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getCustomerById(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @typedef {object} Options
 * @property {string} baseUrl
 * @property {import('types').BankId} bankId
 * @property {string} [token]
 * @property {string} [userTrackingId]
 * @property {string} [customerId]
 * @property {any} [body]
 */

/**
 * This will use update customer details for those customer who have done ID & V check
 * @param {Options} options
 */

var updatePersonalCustomer = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var baseUrl, bankId, token, userTrackingId, customerId, body, validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            baseUrl = _ref.baseUrl, bankId = _ref.bankId, token = _ref.token, userTrackingId = _ref.userTrackingId, customerId = _ref.customerId, body = _ref.body;
            _context.prev = 1;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 6;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 6;
              break;
            }

            return _context.abrupt("return", validations);

          case 6:
            apic = initLegacyApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token,
              'x-bpi-version': X_1_0_0
            }));
            path = endpointParser$3(updatePersonalCustomerPath, bankId, customerId);
            _context.next = 12;
            return apic.put(path, body);

          case 12:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 16:
            _context.prev = 16;
            _context.t0 = _context["catch"](1);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 16]]);
  }));

  return function updatePersonalCustomer(_x) {
    return _ref2.apply(this, arguments);
  };
}();

/**
 * @typedef {object} Options
 * @property {string} baseUrl
 * @property {import('types').BankId} bankId
 * @property {string} [token]
 * @property {string} [userTrackingId]
 * @property {string} [customerId]
 * @property {any} [body]
 */

/**
 * This will use update customer details for those customer who have not done ID & V check
 * @param {Options} options
 */

var updatePersonalCustomerPersonalDetails = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var baseUrl, bankId, token, userTrackingId, customerId, body, validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            baseUrl = _ref.baseUrl, bankId = _ref.bankId, token = _ref.token, userTrackingId = _ref.userTrackingId, customerId = _ref.customerId, body = _ref.body;
            _context.prev = 1;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 6;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 6;
              break;
            }

            return _context.abrupt("return", validations);

          case 6:
            apic = initLegacyApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token,
              'x-bpi-version': X_1_0_0
            }));
            path = endpointParser$3(updatePersonalCustomerPersonalDetailsPath, bankId, customerId);
            _context.next = 12;
            return apic.put(path, body);

          case 12:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 16:
            _context.prev = 16;
            _context.t0 = _context["catch"](1);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 16]]);
  }));

  return function updatePersonalCustomerPersonalDetails(_x) {
    return _ref2.apply(this, arguments);
  };
}();

/**
 * This will use to create personal customer
 *
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userTrackingId
 * @param {object} body
 */

var createPersonalCustomer = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initLegacyApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0
            })); // @ts-ignore

            path = endpointParser$3(createPersonalCustomerPath, bankId);
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function createPersonalCustomer(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

var index$4 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getCustomerList: getCustomerList,
  getCustomerById: getCustomerById,
  updatePersonalCustomer: updatePersonalCustomer,
  updatePersonalCustomerPersonalDetails: updatePersonalCustomerPersonalDetails,
  createPersonalCustomer: createPersonalCustomer
});

var getUserInsightsEndPoint="/banks/:bankId/insights/settings/user";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 */

var getUserInsights = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = getUserInsightsEndPoint.replace(':bankId', bankId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getUserInsights(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

var index$5 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getUserInsights: getUserInsights
});

var eligibilityRefDataEndPoint="/banks/:bankId/ref/content/productinfo/current?is_overdraft=true";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 */

var getEligibleRefData = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0
            }));
            path = eligibilityRefDataEndPoint.replace(':bankId', bankId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getEligibleRefData(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();

var index$6 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEligibleRefData: getEligibleRefData
});

var secureMessagingEndPoint="/banks/:bankId/securemessages";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 */

var getSecureMessages = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = secureMessagingEndPoint.replace(':bankId', bankId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getSecureMessages(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

var index$7 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getSecureMessages: getSecureMessages
});

var tokenChallengeEndpoint="/banks/:bankId/auth/provider/oauth2/token/challenges";var accessTokenEndpoint="/banks/:bankId/auth/provider/oauth2/token";var getPublicKeyEndpoint="/banks/:bankId/auth/provider/rsa/public_key";

/**
 * todo add the link to the endpoint docs here
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userId
 * @param {any} scope
 */

var createTokenChallenge = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userId, scope) {
    var validations, apic, payload, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initLegacyApiConnector(baseUrl, bankId);
            payload = {
              user_identity: {
                user_id: userId
              },
              scope: scope
            };
            path = tokenChallengeEndpoint.replace(':bankId', bankId);
            _context.next = 10;
            return apic.post(path, payload);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);

            /** @todo fix this */
            // eslint-disable-next-line no-console
            console.error(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));

  return function createTokenChallenge(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {any} grantType
 * @param {string | number} scope
 * @param {string} token
 */

var createAccessToken = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, grantType, scope, token) {
    var validations, apic, payload, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initLegacyApiConnector(baseUrl, bankId);
            apic.setHeaders(_objectSpread(_objectSpread({}, apic.headers), {}, {
              'content-type': 'application/x-www-form-urlencoded',
              Authorization: "Basic " + token
            }));
            payload = "grant_type=" + grantType + "&scope=" + scope;
            path = accessTokenEndpoint.replace(':bankId', bankId);
            _context.next = 11;
            return apic.post(path, payload);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);

            /** @todo fix this */
            // eslint-disable-next-line no-console
            console.error(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function createAccessToken(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 */

var getPublicKey = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0
            }));
            path = getPublicKeyEndpoint.replace(':bankId', bankId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getPublicKey(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();

var index$8 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createTokenChallenge: createTokenChallenge,
  createAccessToken: createAccessToken,
  getPublicKey: getPublicKey
});

var userPreferencesEndpoint="/user/preferences";var userTokenEndpoint="/user/token";var userDetailsEndpoint="/user/details";var getCurrentUserEndpoint="/user";var getCredentialsEndpoint="/banks/:bankId/user/credentials";

var endpointParser$4 = function endpointParser() {
  return userPreferencesEndpoint;
};
/**
 * @param {string} endpoint
 * @param {import('types').BankId} bankId
 */

var endpointParserForCredentials = function endpointParserForCredentials(endpoint, bankId) {
  return endpoint.replace(':bankId', bankId);
};

/**
 * Gets the user preferences
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @returns {import('types').ApiResponse<import('..').UserPreferences>}
 */

function getUserPreferences(_x, _x2, _x3) {
  return _getUserPreferences.apply(this, arguments);
}

function _getUserPreferences() {
  _getUserPreferences = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = endpointParser$4();
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getUserPreferences.apply(this, arguments);
}

var yesNoValues = ['Y', 'N'];
/**
 * @param {import("src/services/user/methods").UserPreferences} userPreferences
 */

var validateUserPreferences = function validateUserPreferences(userPreferences) {
  var userPreferencesSchema = object().shape({
    marketing_preferences: array().of(object({
      type: string(),
      value: string().oneOf(yesNoValues)
    })).notRequired(),
    communication_preferences: array().of(object({
      type: string(),
      value: string().oneOf(yesNoValues),
      accounts: array().of(object({
        id: string().test(isGuid)
      })).notRequired()
    })).notRequired()
  }).noUnknown().strict(true); // @ts-ignore
  // Some oddness with this one as `notRequired` should allow this to be set as nullable.

  return validate(userPreferencesSchema, userPreferences);
};

/**
 * Gets the user preferences
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('..').UserPreferences} preferences
 */

function setUserPreferences(_x, _x2, _x3, _x4) {
  return _setUserPreferences.apply(this, arguments);
}

function _setUserPreferences() {
  _setUserPreferences = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, preferences) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserPreferences(preferences));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = endpointParser$4();
            _context.next = 11;
            return apic.put(path, preferences);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _setUserPreferences.apply(this, arguments);
}

/**
 * Gets the user preferences
 * @param {string} baseUrl
 * @param {string} token
 * @param {string} userTrackingId
 */

function getUserBankIdByToken(_x, _x2, _x3) {
  return _getUserBankIdByToken.apply(this, arguments);
}

function _getUserBankIdByToken() {
  _getUserBankIdByToken = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, token, userTrackingId) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl), validateAccessToken(token), validateUserTrackingId(userTrackingId)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            // @ts-ignore
            apic = initApiConnector(baseUrl, undefined);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.get(userTokenEndpoint);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _getUserBankIdByToken.apply(this, arguments);
}

/**
 * Gets the user details
 * @param {string} baseUrl
 * @param {string} token
 */

function getUserDetails(_x, _x2) {
  return _getUserDetails.apply(this, arguments);
}

function _getUserDetails() {
  _getUserDetails = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, token) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl), validateAccessToken(token)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            // @ts-ignore
            apic = initApiConnector(baseUrl, undefined);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.get(userDetailsEndpoint);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _getUserDetails.apply(this, arguments);
}

/**
 * Gets the current user details viz username and contact details
 * @param {string} baseUrl
 * @param {string} token
 * @param {string} userTrackingId
 */

function getCurrentUser(_x, _x2, _x3) {
  return _getCurrentUser.apply(this, arguments);
}

function _getCurrentUser() {
  _getCurrentUser = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, token, userTrackingId) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl), validateAccessToken(token), validateUserTrackingId(userTrackingId)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            // @ts-ignore
            apic = initApiConnector(baseUrl, undefined);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_0_8_0,
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.get(getCurrentUserEndpoint);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _getCurrentUser.apply(this, arguments);
}

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 */

var getCredentials = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_0_8_0,
              Authorization: "Bearer " + token
            }));
            path = endpointParserForCredentials(getCredentialsEndpoint, bankId);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getCredentials(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

var index$9 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getUserPreferences: getUserPreferences,
  setUserPreferences: setUserPreferences,
  getUserBankIdByToken: getUserBankIdByToken,
  getUserDetails: getUserDetails,
  getCurrentUser: getCurrentUser,
  getCredentials: getCredentials
});

var getAllVerificationsEndPoint="/banks/:bankId/user/:scope/verification/all";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 */

var getAllVerifications = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = getAllVerificationsEndPoint.replace(':bankId', bankId).replace(':scope', 'default');
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getAllVerifications(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

var index$a = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getAllVerifications: getAllVerifications
});

var searchRecordsRMPath="/record/search";var getApplicationRMPath="/application/:decisionRef";var addCommentRMPath="/application/:decisionRef/comment";

/**
 * @param {string} endpoint
 * @param {string} decisionRef
 */
var endpointParser$5 = function endpointParser(endpoint, decisionRef) {
  return endpoint.replace(':decisionRef', decisionRef);
};

/**
 * Gets the user details
 * @param {string} baseUrl
 * @param {string} decisionReference
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} customerNumber
 * @param {number} apiTrustLevel
 * */

function getApplicationByDecisionReference(_x, _x2, _x3, _x4, _x5, _x6) {
  return _getApplicationByDecisionReference.apply(this, arguments);
}

function _getApplicationByDecisionReference() {
  _getApplicationByDecisionReference = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, decisionReference, bankId, token, customerNumber, apiTrustLevel) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateDecisionReference(decisionReference));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnectorStaffOnly(baseUrl, bankId, customerNumber, apiTrustLevel);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = endpointParser$5(getApplicationRMPath, decisionReference);
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getApplicationByDecisionReference.apply(this, arguments);
}

/**
 * Gets the user details
 * @param {string} baseUrl
 * @param {object} body
 * @param {string} decisionReference
 * @param {string} token
 * @param {import('types').BankId} bankId
 * @param {string} customerNumber
 * @param {number} apiTrustLevel
 */

function addComment(_x, _x2, _x3, _x4, _x5, _x6, _x7) {
  return _addComment.apply(this, arguments);
}

function _addComment() {
  _addComment = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, body, decisionReference, token, bankId, customerNumber, apiTrustLevel) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateDecisionReference(decisionReference));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnectorStaffOnly(baseUrl, bankId, customerNumber, apiTrustLevel);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = endpointParser$5(addCommentRMPath, decisionReference);
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _addComment.apply(this, arguments);
}

/**
 * Gets the user details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {object} body
 * @param {string} token
 * @param {string} customerNumber
 * @param {number} apiTrustLevel
 */

function searchRecords(_x, _x2, _x3, _x4, _x5, _x6) {
  return _searchRecords.apply(this, arguments);
}

function _searchRecords() {
  _searchRecords = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, body, bankId, token, customerNumber, apiTrustLevel) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnectorStaffOnly(baseUrl, bankId, customerNumber, apiTrustLevel);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.put(searchRecordsRMPath, body);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _searchRecords.apply(this, arguments);
}

var index$b = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getApplicationByDecisionReference: getApplicationByDecisionReference,
  addComment: addComment,
  searchRecords: searchRecords
});

var requestCustomerNumberBySCAN="/banks/customers/customernumber/search";

/**
 * Looks up customer number to send an SMS notification
 * @param {string} baseUrl
 * @param {object} body
 * @param {string} appId
 */

function requestCustomerNumberBySCAN$1(_x, _x2, _x3) {
  return _requestCustomerNumberBySCAN.apply(this, arguments);
}

function _requestCustomerNumberBySCAN() {
  _requestCustomerNumberBySCAN = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, body, appId) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateNoBrandCommonMethodParams(baseUrl);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initNoBrandApiConnector(baseUrl, appId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread({}, headers));
            _context.next = 10;
            return apic.post(requestCustomerNumberBySCAN, body);

          case 10:
            resp = _context.sent;

            if (!resp.ok) {
              _context.next = 13;
              break;
            }

            return _context.abrupt("return", resp.ok);

          case 13:
            return _context.abrupt("return", parseError(resp));

          case 16:
            _context.prev = 16;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", {
              error: _context.t0.message || API_REQUEST_ERROR
            });

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 16]]);
  }));
  return _requestCustomerNumberBySCAN.apply(this, arguments);
}

var index$c = /*#__PURE__*/Object.freeze({
  __proto__: null,
  requestCustomerNumberBySCAN: requestCustomerNumberBySCAN$1
});

export { index as accountService, index$1 as addressSearch, index$2 as beneficiaryManagement, index$3 as caseService, index$c as customerAssist, index$4 as customerService, index$5 as insightManagement, index$b as recordManagement, index$6 as referenceDataService, index$7 as secureMessaging, index$8 as userAuth, index$9 as userService, index$a as userVerification };
